import { useState, useEffect, useRef } from 'react';
import { ListItem } from '../types';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';

interface SupermarketData {
  id: string;
  chain: string;
  name?: string;
  street: string;
  zipCode: string;
  city: string;
  zones: {
    id: string;
    name: string;
    icon?: string;
    order: number;
    categoryMappings: {
      id: string;
      categoryId: string;
      categoryName: string;
    }[];
  }[];
}

interface SupermarketViewProps {
  supermarket: SupermarketData;
  items: ListItem[];
  onToggleItem: (itemId: string, isChecked: boolean) => void;
  onToggleUnavailable: (itemId: string, isUnavailable: boolean) => void;
  onEditItem: (itemId: string) => void;
}

interface GroupedItems {
  [zoneId: string]: ListItem[];
}

export function SupermarketView({ supermarket, items, onToggleItem, onToggleUnavailable, onEditItem }: SupermarketViewProps) {
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const [lastTapTime, setLastTapTime] = useState<number>(0);
  const [lastTapItemId, setLastTapItemId] = useState<string | null>(null);
  const tapTimerRef = useRef<number | null>(null);
  
  // Group items by zone
  const groupItemsByZone = (): GroupedItems => {
    const grouped: GroupedItems = {};
    
    // Initialize zones
    supermarket.zones.forEach(zone => {
      grouped[zone.id] = [];
    });
    
    // Ungrouped items
    grouped['ungrouped'] = [];
    
    // Assign items to zones (filter out unavailable)
    items.filter(item => !item.isUnavailable).forEach(item => {
      if (!item.category) {
        grouped['ungrouped'].push(item);
        return;
      }
      
      // Find zone that contains this category
      const zone = supermarket.zones.find(z => 
        z.categoryMappings.some(m => m.categoryName === item.category)
      );
      
      if (zone) {
        grouped[zone.id].push(item);
      } else {
        grouped['ungrouped'].push(item);
      }
    });
    
    return grouped;
  };

  const groupedItems = groupItemsByZone();
  
  // Calculate progress
  const totalZones = supermarket.zones.filter(z => groupedItems[z.id]?.length > 0).length;
  const completedZones = supermarket.zones.filter(z => {
    const zoneItems = groupedItems[z.id] || [];
    return zoneItems.length > 0 && zoneItems.every(item => item.isChecked);
  }).length;
  
  const toggleZone = (zoneId: string) => {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  const handleItemClick = (item: ListItem) => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;
    
    // Double-Tap Detection (innerhalb 300ms)
    if (timeDiff < 300 && lastTapItemId === item.id) {
      // Double-Tap erkannt - Abbrechen des pending single-tap
      if (tapTimerRef.current) {
        clearTimeout(tapTimerRef.current);
        tapTimerRef.current = null;
      }
      
      // Toggle Unavailable
      onToggleUnavailable(item.id, item.isUnavailable || false);
      setLastTapTime(0);
      setLastTapItemId(null);
    } else {
      // Single-Tap - Verzögert ausführen (auf Double-Tap warten)
      setLastTapTime(now);
      setLastTapItemId(item.id);
      
      // Bestehenden Timer abbrechen
      if (tapTimerRef.current) {
        clearTimeout(tapTimerRef.current);
      }
      
      // Neuen Timer für Single-Tap setzen
      tapTimerRef.current = setTimeout(() => {
        onToggleItem(item.id, item.isChecked);
        tapTimerRef.current = null;
      }, 300);
    }
  };
  
  // Auto-expand first incomplete zone on mount or when items change
  useEffect(() => {
    const grouped = groupItemsByZone();
    const firstIncompleteZone = supermarket.zones.find(z => {
      const zoneItems = grouped[z.id] || [];
      return zoneItems.length > 0 && !zoneItems.every(item => item.isChecked);
    });
    
    if (firstIncompleteZone) {
      setExpandedZones(prev => {
        if (!prev.has(firstIncompleteZone.id)) {
          return new Set([firstIncompleteZone.id]);
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, items.filter(i => i.isChecked).length]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Progress Bar */}
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        padding: '1rem',
        borderRadius: '12px',
        boxShadow: 'var(--shadow)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.75rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>🛒</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
              {supermarket.chain} {supermarket.name && `(${supermarket.name})`}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
              {supermarket.street}, {supermarket.zipCode} {supermarket.city}
            </div>
          </div>
        </div>
        
        {/* Progress */}
        <div style={{
          height: '8px',
          backgroundColor: 'var(--gray-200)',
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '0.5rem'
        }}>
          <div style={{
            height: '100%',
            backgroundColor: 'var(--success)',
            width: `${totalZones > 0 ? (completedZones / totalZones) * 100 : 0}%`,
            transition: 'width 0.3s ease'
          }} />
        </div>
        
        <div style={{
          fontSize: '0.875rem',
          color: 'var(--gray-600)',
          textAlign: 'center'
        }}>
          {completedZones} von {totalZones} Bereichen abgeschlossen
        </div>
      </div>

      {/* Zones */}
      {supermarket.zones.map((zone, zoneIndex) => {
        const zoneItems = groupedItems[zone.id] || [];
        
        if (zoneItems.length === 0) return null;
        
        const uncheckedCount = zoneItems.filter(i => !i.isChecked).length;
        const isCompleted = zoneItems.every(item => item.isChecked);
        const isExpanded = expandedZones.has(zone.id);
        const isActive = !isCompleted && zoneIndex === supermarket.zones.findIndex(z => {
          const items = groupedItems[z.id] || [];
          return items.length > 0 && !items.every(i => i.isChecked);
        });

        return (
          <div
            key={zone.id}
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: 'var(--shadow)',
              border: isCompleted ? '2px solid var(--success)' : isActive ? '2px solid var(--primary)' : '2px solid transparent',
              opacity: isCompleted ? 0.7 : 1,
              transition: 'all 0.2s'
            }}
          >
            {/* Zone Header */}
            <button
              onClick={() => toggleZone(zone.id)}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: isCompleted ? 'var(--success-light)' : isActive ? 'var(--info-light)' : 'var(--gray-50)',
                borderBottom: isExpanded ? '1px solid var(--gray-200)' : 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'background-color 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                <span style={{ fontSize: '1.5rem' }}>{zone.icon || '📦'}</span>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    fontSize: '1rem',
                    color: 'var(--gray-900)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {isActive && <span style={{ 
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      fontSize: '0.75rem',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '12px',
                      fontWeight: '700'
                    }}>AKTIV</span>}
                    Zone {zoneIndex + 1}: {zone.name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                    {uncheckedCount > 0 ? (
                      `${uncheckedCount} Artikel offen`
                    ) : (
                      <span style={{ color: 'var(--success)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Check size={16} /> Fertig
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {isCompleted && (
                  <span style={{ fontSize: '1.5rem', color: 'var(--success)' }}>✓</span>
                )}
                {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </div>
            </button>

            {/* Items in Zone */}
            {isExpanded && (
              <div style={{ padding: '0.5rem' }}>
                {zoneItems.map(item => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s, opacity 0.2s',
                      textDecoration: item.isChecked ? 'line-through' : 'none',
                      opacity: item.isChecked ? 0.6 : item.isUnavailable ? 0.5 : 1,
                      backgroundColor: item.isUnavailable ? 'var(--gray-100)' : 'transparent'
                    }}
                    onClick={() => handleItemClick(item)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={item.isChecked}
                      onChange={() => onToggleItem(item.id, item.isChecked)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '1.25rem',
                        height: '1.25rem',
                        cursor: 'pointer',
                        accentColor: 'var(--success)'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: 'var(--gray-900)' }}>
                        {item.name}
                      </div>
                      {item.quantity && (
                        <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                          {item.quantity}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditItem(item.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.25rem',
                        color: 'var(--gray-600)',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      ⋮
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Ungrouped Items */}
      {groupedItems['ungrouped']?.length > 0 && (
        <div style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '1rem',
          boxShadow: 'var(--shadow)'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--gray-600)',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            📦 Nicht zugeordnet ({groupedItems['ungrouped'].length})
          </h3>
          
          {groupedItems['ungrouped'].map(item => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s, opacity 0.2s',
                textDecoration: item.isChecked ? 'line-through' : 'none',
                opacity: item.isChecked ? 0.6 : item.isUnavailable ? 0.5 : 1,
                backgroundColor: item.isUnavailable ? 'var(--gray-100)' : 'transparent'
              }}
              onClick={() => handleItemClick(item)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <input
                type="checkbox"
                checked={item.isChecked}
                onChange={() => onToggleItem(item.id, item.isChecked)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  cursor: 'pointer',
                  accentColor: 'var(--success)'
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '500', color: 'var(--gray-900)' }}>
                  {item.name}
                </div>
                {item.quantity && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    {item.quantity}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditItem(item.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.25rem',
                  color: 'var(--gray-600)',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                ⋮
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Unavailable Items */}
      {items.filter(item => item.isUnavailable).length > 0 && (
        <div style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '1rem',
          boxShadow: 'var(--shadow)',
          marginTop: '1rem'
        }}>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--gray-600)',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🚫 Nicht verfügbar ({items.filter(item => item.isUnavailable).length})
          </h3>
          
          {items.filter(item => item.isUnavailable).map(item => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                backgroundColor: 'var(--gray-100)',
                opacity: 0.7,
                marginBottom: '0.5rem'
              }}
              onClick={() => handleItemClick(item)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-200)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '500', color: 'var(--gray-700)' }}>
                  {item.name}
                </div>
                {item.quantity && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    {item.quantity}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditItem(item.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.25rem',
                  color: 'var(--gray-600)',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                ⋮
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
